import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetDashboardQuery } from '../redux/apiSlice';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const [mounted, setMounted] = useState(false);
  const navigate = useNavigate();
  
  // Use the dashboard query hook
  const { 
    data: dashboardData, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useGetDashboardQuery(undefined, {
    refetchOnMountOrArgChange: true,
    pollingInterval: 300000, // Refetch every 5 minutes
  });

  useEffect(() => {
    setMounted(true);
    
    // Debug: Log dashboard data structure
    if (dashboardData) {
      console.log('📊 Full dashboard data:', dashboardData);
      console.log('📊 Recent courses structure:', dashboardData.recentCourses);
      
      // Log each course to see what properties are available
      if (dashboardData.recentCourses && dashboardData.recentCourses.length > 0) {
        dashboardData.recentCourses.forEach((course, index) => {
          console.log(`📊 Course ${index + 1}:`, {
            id: course.id,
            _id: course._id,
            courseId: course.courseId,
            title: course.title,
            slug: course.slug,
            allKeys: Object.keys(course)
          });
        });
      }
    }
    
    // Show error toast if API fails
    if (isError) {
      toast.error(error?.message || 'Failed to load dashboard data', {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
    
    // Show success message if data loads successfully
    if (dashboardData?.success && dashboardData?.message) {
      toast.success(dashboardData.message, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: true,
      });
    }
  }, [isError, error, dashboardData]);

  // Handle retry fetching data
  const handleRetry = () => {
    refetch();
    toast.info('Refreshing dashboard data...', {
      position: "top-right",
      autoClose: 2000,
    });
  };

  // Handle navigation
  const handleViewAllCourses = () => {
    navigate('/courses');
  };

  const handleViewAllTransactions = () => {
    navigate('/earnings');
  };

  // Handle course click with proper ID extraction
  const handleCourseClick = (course) => {
    console.log('🔍 Clicked course object:', course);
    console.log('🔍 Available IDs:', {
      id: course.id,
      _id: course._id,
      courseId: course.courseId,
      slug: course.slug
    });
    
    // Try to get the course ID from various possible fields
    const courseId = course.id || course._id || course.courseId;
    
    if (!courseId) {
      console.error('❌ No valid course ID found in course object:', course);
      toast.error('Invalid course ID. Cannot open course details.', {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }
    
    console.log('✅ Navigating to course detail with ID:', courseId);
    navigate(`/courses/coursedetail/${courseId}`);
  };

  // Extract data from API response or use defaults
  const statsData = dashboardData?.statsData || {
    totalStudents: 0,
    totalCourses: 0,
    totalEarnings: 0,
    pendingCourses: 0
  };
  
  const recentCourses = dashboardData?.recentCourses || [];
  const recentTransactions = dashboardData?.recentTransactions || [];

  // Stats with icons and colors
  const stats = [
    {
      label: 'Total Students',
      value: statsData.totalStudents.toLocaleString(),
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
        </svg>
      ),
      color: 'green',
      borderColor: 'border-green-500',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600'
    },
    {
      label: 'Total Courses',
      value: statsData.totalCourses,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      color: 'blue',
      borderColor: 'border-blue-500',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600'
    },
    {
      label: 'Total Earnings',
      value: `$${statsData.totalEarnings.toLocaleString()}`,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
        </svg>
      ),
      color: 'emerald',
      borderColor: 'border-emerald-500',
      bgColor: 'bg-emerald-50',
      textColor: 'text-emerald-600'
    },
    {
      label: 'Pending Courses',
      value: statsData.pendingCourses,
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      color: 'yellow',
      borderColor: 'border-yellow-500',
      bgColor: 'bg-yellow-50',
      textColor: 'text-yellow-600'
    }
  ];

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          {/* Welcome Section Skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-300 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          </div>
          
          {/* Stats Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-xl bg-gray-200 w-12 h-12"></div>
                  <div className="ml-4">
                    <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                    <div className="h-6 bg-gray-300 rounded w-16"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Content Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="h-6 bg-gray-300 rounded w-1/4 mb-6"></div>
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl mb-4">
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                  </div>
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="h-6 bg-gray-300 rounded w-1/4 mb-6"></div>
              {[1, 2].map(i => (
                <div key={i} className="flex items-center justify-between p-4 border border-gray-200 rounded-xl mb-4">
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                  </div>
                  <div className="text-right">
                    <div className="h-6 bg-gray-200 rounded w-12 mb-2 ml-auto"></div>
                    <div className="h-4 bg-gray-100 rounded w-16 ml-auto"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError && !dashboardData) {
    return (
      <div className="p-6 flex flex-col items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="text-red-500 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Failed to Load Dashboard Please Login Again you session has Expire</h2>
          <p className="text-gray-600 mb-6">
            {error?.data?.message || error?.message || 'An error occurred while loading dashboard data'}
          </p>
          <button
            onClick={handleRetry}
            className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors duration-300 flex items-center mx-auto"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 animate-fade-in">
      {/* Welcome Section with Refresh Button */}
      <div className={`mb-8 transform transition-all duration-700 ease-out ${
        mounted ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
      }`}>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2 animate-fade-in-up">
              Welcome back, Instructor!
            </h1>
            <p className="text-gray-600 text-lg animate-fade-in-up delay-200">
              Here's what's happening with your courses today.
            </p>
          </div>
          <button
            onClick={handleRetry}
            className="px-4 py-2 bg-green-50 text-green-700 font-medium rounded-lg hover:bg-green-100 transition-colors duration-300 flex items-center group"
            title="Refresh dashboard data"
          >
            <svg className={`w-5 h-5 mr-2 group-hover:rotate-180 transition-transform duration-500 ${isLoading ? 'animate-spin' : ''}`} 
                 fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div 
            key={stat.label}
            className={`bg-white rounded-2xl shadow-lg p-6 border-l-4 ${stat.borderColor} transform transition-all duration-500 ease-out hover:scale-105 hover:shadow-xl ${
              mounted ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="flex items-center">
              <div className={`p-3 rounded-xl ${stat.bgColor} ${stat.textColor} shadow-md`}>
                {stat.icon}
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Courses */}
        <div className={`bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-700 ease-out ${
          mounted ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'
        }`}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800 break-words">Recent Courses</h2>
              {recentCourses.length > 0 && (
                <button 
                  onClick={handleViewAllCourses}
                  className="text-green-600 hover:text-green-700 font-medium text-sm transition-all duration-300 hover:scale-105 flex items-center space-x-1 group flex-shrink-0"
                >
                  <span>View All</span>
                  <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
            {recentCourses.length === 0 ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <p className="text-gray-500 break-words">No courses available</p>
                <button 
                  onClick={() => navigate('/courses/create')}
                  className="mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-300"
                >
                  Create Your First Course
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {recentCourses.map((course, index) => {
                  // Get the display ID for debugging
                  const displayId = course.id || course._id || course.courseId;
                  
                  return (
                    <div 
                      key={course.id || course._id || course.courseId || index}
                      className="flex items-start justify-between p-4 border border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-all duration-300 group cursor-pointer overflow-hidden"
                      style={{ animationDelay: `${index * 150}ms` }}
                      onClick={() => handleCourseClick(course)}
                    >
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors duration-300 break-words overflow-hidden">
                          {course.title}
                        </h3>
                        <div className="flex items-center mt-2 text-sm text-gray-600 space-x-3 flex-wrap">
                          <span className="flex items-center space-x-1 flex-shrink-0">
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                            </svg>
                            <span className="break-words">{course.students || 0} students</span>
                          </span>
                          <span className="text-gray-300 flex-shrink-0">•</span>
                          <span className="break-words flex-shrink-0">{course.lastUpdated || 'Just now'}</span>
                          {/* Debug info - remove in production */}
                          <span className="text-xs text-gray-400 ml-2">(ID: {displayId || 'N/A'})</span>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold transition-all duration-300 flex-shrink-0 ml-3 ${
                        course.status === 'PUBLISHED' ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                        course.status === 'DRAFT' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                        'bg-blue-100 text-blue-800 hover:bg-blue-200'
                      }`}>
                        {course.status || 'DRAFT'}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className={`bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-700 ease-out delay-200 ${
          mounted ? 'translate-y-0 opacity-100 scale-100' : 'translate-y-8 opacity-0 scale-95'
        }`}>
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-gray-800 break-words">Recent Transactions</h2>
              {recentTransactions.length > 0 && (
                <button 
                  onClick={handleViewAllTransactions}
                  className="text-green-600 hover:text-green-700 font-medium text-sm transition-all duration-300 hover:scale-105 flex items-center space-x-1 group flex-shrink-0"
                >
                  <span>View All</span>
                  <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform duration-300 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              )}
            </div>
            {recentTransactions.length === 0 ? (
              <div className="text-center py-8">
                <svg className="w-12 h-12 text-gray-300 mx-auto mb-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                <p className="text-gray-500 break-words">No transactions yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recentTransactions.map((transaction, index) => (
                  <div 
                    key={transaction.id || index}
                    className="flex items-start justify-between p-4 border border-gray-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-all duration-300 group overflow-hidden"
                    style={{ animationDelay: `${index * 150}ms` }}
                  >
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 group-hover:text-green-700 transition-colors duration-300 break-words overflow-hidden">
                        {transaction.course}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1 flex items-center space-x-1 flex-wrap">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="break-words">{transaction.date}</span>
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0 ml-3">
                      <p className="font-bold text-gray-900 text-lg break-words">${transaction.amount}</p>
                      <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                        transaction.status === 'COMPLETED' 
                          ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                          : 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                      } transition-colors duration-300 inline-block mt-1`}>
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.8s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;